<template>
  <v-container fluid class="pa-6 info-page">
    <!-- Header -->
    <div class="page-header mb-6">
      <div class="header-icon">
        <v-icon color="white" size="28">fas fa-id-card</v-icon>
      </div>
      <div class="header-text">
        <h1 class="page-title">Personal Information</h1>
        <p class="page-subtitle">Update only what you want to change</p>
      </div>
    </div>

    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card class="info-card" flat>
          <v-card-text class="pa-6">
            <v-form ref="infoForm" lazy-validation>

              <!-- Bio Section -->
              <div class="section-block mb-6">
                <h3 class="section-label">About You</h3>
                <v-textarea
                  v-model="description"
                  placeholder="Share a brief description of your background, interests, and what you do..."
                  outlined
                  rows="3"
                  auto-grow
                  class="custom-textarea"
                  counter="500"
                  maxlength="500"
                ></v-textarea>
              </div>

              <v-divider class="mb-6"></v-divider>

              <!-- Details Grid -->
              <div class="section-block">
                <h3 class="section-label">Details</h3>

                <v-row>
                  <!-- Gender -->
                  <v-col cols="12" sm="6">
                    <div class="field-wrapper">
                      <label class="field-label">Gender</label>
                      <v-select
                        v-model="gender"
                        :items="genderOptions"
                        placeholder="Select gender"
                        outlined
                        dense
                        clearable
                        height="52"
                        class="custom-select"
                        prepend-inner-icon="fas fa-venus-mars"
                      ></v-select>
                    </div>
                  </v-col>

                  <!-- Profession -->
                  <v-col cols="12" sm="6">
                    <div class="field-wrapper">
                      <label class="field-label">Profession</label>
                      <v-text-field
                        v-model="profession"
                        placeholder="e.g. Software Engineer"
                        outlined
                        dense
                        clearable
                        height="52"
                        class="custom-field"
                        prepend-inner-icon="fas fa-briefcase"
                      ></v-text-field>
                    </div>
                  </v-col>

                  <!-- Language -->
                  <v-col cols="12" sm="6">
                    <div class="field-wrapper">
                      <label class="field-label">Preferred Language</label>
                      <v-text-field
                        v-model="language"
                        placeholder="e.g. English, French"
                        outlined
                        dense
                        clearable
                        height="52"
                        class="custom-field"
                        prepend-inner-icon="fas fa-language"
                      ></v-text-field>
                    </div>
                  </v-col>

                  <!-- Residence -->
                  <v-col cols="12" sm="6">
                    <div class="field-wrapper">
                      <label class="field-label">Residential Address</label>
                      <v-text-field
                        v-model="residence"
                        placeholder="City, Country"
                        outlined
                        dense
                        clearable
                        height="52"
                        class="custom-field"
                        prepend-inner-icon="fas fa-map-marker-alt"
                      ></v-text-field>
                    </div>
                  </v-col>

                  <!-- Date of Birth -->
                  <v-col cols="12" sm="6">
                    <div class="field-wrapper">
                      <label class="field-label">Date of Birth</label>
                      <v-menu
                        ref="dateMenu"
                        v-model="dateMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="birthday"
                            placeholder="Select date (optional)"
                            outlined
                            dense
                            clearable
                            height="52"
                            class="custom-field"
                            prepend-inner-icon="fas fa-calendar-alt"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            @click:clear="birthday = ''"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="birthday"
                          color="#1e40af"
                          @input="dateMenu = false"
                          max="2010-01-01"
                          min="1940-01-01"
                        ></v-date-picker>
                      </v-menu>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- Action Buttons -->
              <div class="action-section mt-8">
                <v-btn
                  text
                  color="#64748b"
                  class="mr-3 cancel-btn"
                  @click="resetForm"
                  :disabled="isLoading"
                >
                  Reset
                </v-btn>

                <v-btn
                  color="#1e40af"
                  dark
                  height="48"
                  class="save-btn"
                  :loading="isLoading"
                  :disabled="!hasChanges || isLoading"
                  @click="updateAccountDetails"
                >
                  <v-icon left small class="mr-2">fas fa-save</v-icon>
                  Save Information
                </v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>

        <!-- Privacy Notice -->
        <v-card class="privacy-card mt-4" flat>
          <v-card-text class="pa-4 d-flex align-start">
            <v-icon color="#1e40af" class="mr-3 mt-1">fas fa-shield-alt</v-icon>
            <div>
              <span class="privacy-title">Privacy Notice</span>
              <p class="privacy-text mb-0">
                This information helps us personalize your experience. Your data is encrypted and never shared with third parties without your consent.
              </p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Success Dialog — icône non coupée grâce au padding-top sur le card -->
    <v-dialog v-model="showSuccess" max-width="400" persistent>
      <v-card class="success-dialog">
        <!-- Wrapper externe qui crée l'espace pour l'icône flottante -->
        <div class="success-icon-spacer">
          <div class="success-icon-wrapper">
            <v-icon color="white" size="40">fas fa-check</v-icon>
          </div>
        </div>
        <v-card-title class="justify-center pb-2">
          <span class="success-title">Profile Updated!</span>
        </v-card-title>
        <v-card-text class="text-center pb-4">
          <p class="success-text">Your personal information has been saved successfully.</p>
        </v-card-text>
        <v-card-actions class="justify-center pb-6">
          <v-btn color="#1e40af" dark @click="showSuccess = false" class="px-6">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'

export default {
  name: "PersonalInfo",

  data() {
    return {
      isLoading: false,
      showSuccess: false,
      dateMenu: false,
      hasChanges: false,

      // Form fields
      description: "",
      gender: "",
      profession: "",
      language: "",
      residence: "",
      birthday: "",

      // Original data for comparison
      originalData: {},

      genderOptions: [
        { text: 'Male', value: 'Male' },
        { text: 'Female', value: 'Female' },
      ]
    }
  },

  watch: {
    description() { this.checkChanges() },
    gender() { this.checkChanges() },
    profession() { this.checkChanges() },
    language() { this.checkChanges() },
    residence() { this.checkChanges() },
    birthday() { this.checkChanges() }
  },

  methods: {
    setAccountDetails() {
      try {
        const userInfo = this.$store.state.user_info
        if (userInfo) {
          this.description = userInfo.user_worker_info?.personal_info || ""
          this.gender     = userInfo.user_profile?.gender    || ""
          this.language   = userInfo.user_profile?.language  || ""
          this.profession = userInfo.user_profile?.profession || ""
          this.residence  = userInfo.user_profile?.address   || ""
          this.birthday   = userInfo.user_profile?.birthday  || ""
          this.saveOriginalData()
        }
      } catch (error) {
        console.error("Error loading user info:", error)
        this.showError("Failed to load your information")
      }
    },

    saveOriginalData() {
      this.originalData = {
        description: this.description,
        gender:      this.gender,
        profession:  this.profession,
        language:    this.language,
        residence:   this.residence,
        birthday:    this.birthday,
      }
    },

    checkChanges() {
      this.hasChanges = (
        this.description !== this.originalData.description ||
        this.gender      !== this.originalData.gender      ||
        this.profession  !== this.originalData.profession  ||
        this.language    !== this.originalData.language    ||
        this.residence   !== this.originalData.residence   ||
        this.birthday    !== this.originalData.birthday
      )
    },

    resetForm() {
      this.description = this.originalData.description
      this.gender      = this.originalData.gender
      this.profession  = this.originalData.profession
      this.language    = this.originalData.language
      this.residence   = this.originalData.residence
      this.birthday    = this.originalData.birthday
      this.hasChanges  = false
      this.$refs.infoForm.resetValidation()
    },

    async updateAccountDetails() {
      // Aucun champ n'est obligatoire — on envoie ce qui a changé
      this.isLoading = true

      try {
        const formData = {
          description: this.description,
          gender:      this.gender,
          profession:  this.profession,
          language:    this.language,
          residence:   this.residence,
          birthday:    this.birthday,
        }

        const response = await axios.post('/api/v1/home/update_account_details/info/', formData)

        if (response.data && response.data[0]) {
          const result = response.data[0]

          if (result.category === "success") {
            this.showSuccess = true
            this.saveOriginalData()
            this.hasChanges = false

            // Mise à jour du store
            if (this.$store.state.user_info?.user_profile) {
              this.$store.state.user_info.user_profile.gender     = this.gender
              this.$store.state.user_info.user_profile.language   = this.language
              this.$store.state.user_info.user_profile.profession = this.profession
              this.$store.state.user_info.user_profile.address    = this.residence
              this.$store.state.user_info.user_profile.birthday   = this.birthday
            }
            if (this.$store.state.user_info?.user_worker_info) {
              this.$store.state.user_info.user_worker_info.personal_info = this.description
            }
          } else {
            this.showError(result.text || "Update failed")
          }
        }
      } catch (error) {
        if (error.response) {
          const messages = Object.values(error.response.data)
          messages.forEach(msg => {
            this.showError(Array.isArray(msg) ? msg[0] : msg)
          })
        } else {
          this.showError("An error occurred while saving your information")
        }
      } finally {
        this.isLoading = false
      }
    },

    showError(message) {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', "error")
      this.$store.commit('activateSnackBar', true)
    }
  },

  mounted() {
    this.setAccountDetails()
    this.$store.commit('setCloseURL', "/")
  }
}
</script>

<style scoped>
.info-page {
  background: #f8fafc;
  min-height: 100vh;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.header-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px -4px rgba(30, 64, 175, 0.3);
}

.header-text {
  display: flex;
  flex-direction: column;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 4px 0 0 0;
  font-weight: 500;
}

.info-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
}

.section-block {
  margin-bottom: 24px;
}

.section-label {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.field-wrapper {
  margin-bottom: 8px;
}

.field-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

/* Custom Textarea */
.custom-textarea >>> .v-input__slot {
  border-radius: 10px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important;
  min-height: 100px !important;
  padding: 12px !important;
  transition: all 0.3s ease !important;
}

.custom-textarea >>> .v-input__slot:hover {
  border-color: #3b82f6 !important;
  box-shadow: 0 4px 12px -2px rgba(59,130,246,0.15) !important;
}

.custom-textarea.v-input--is-focused >>> .v-input__slot {
  border-color: #1e40af !important;
  box-shadow: 0 0 0 3px rgba(30,64,175,0.1) !important;
}

.custom-textarea >>> textarea {
  font-size: 15px;
  color: #0f172a;
  line-height: 1.6;
  margin-top: 0 !important;
}

/* Custom Fields & Select */
.custom-field >>> .v-input__slot,
.custom-select >>> .v-input__slot {
  border-radius: 10px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important;
  min-height: 52px !important;
  transition: all 0.3s ease !important;
}

.custom-field >>> .v-input__slot:hover,
.custom-select >>> .v-input__slot:hover {
  border-color: #3b82f6 !important;
  box-shadow: 0 4px 12px -2px rgba(59,130,246,0.15) !important;
}

.custom-field.v-input--is-focused >>> .v-input__slot,
.custom-select.v-input--is-focused >>> .v-input__slot {
  border-color: #1e40af !important;
  box-shadow: 0 0 0 3px rgba(30,64,175,0.1) !important;
}

.custom-field >>> input,
.custom-select >>> input {
  font-size: 15px;
  color: #0f172a;
  font-weight: 600;
}

/* Action section */
.action-section {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}

.cancel-btn {
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.5px;
  border-radius: 8px;
  height: 48px;
  padding: 0 24px;
}

.cancel-btn:hover {
  background: #f1f5f9;
}

.save-btn {
  background: linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%) !important;
  color: white !important;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0.5px;
  border-radius: 10px;
  padding: 0 28px;
  box-shadow: 0 4px 14px -2px rgba(30,64,175,0.35);
  transition: all 0.3s ease;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px -4px rgba(30,64,175,0.45);
}

.save-btn:disabled {
  background: #cbd5e1 !important;
  box-shadow: none;
}

/* Privacy */
.privacy-card {
  border-radius: 12px;
  border: 1px solid #dbeafe;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
}

.privacy-title {
  font-weight: 700;
  color: #1e40af;
  font-size: 14px;
  display: block;
  margin-bottom: 4px;
}

.privacy-text {
  font-size: 13px;
  color: #1e40af;
  line-height: 1.5;
  opacity: 0.9;
}

/* ===== SUCCESS DIALOG — CORRECTION ICÔNE COUPÉE ===== */
.success-dialog {
  border-radius: 16px;
  overflow: visible !important; /* clé : ne plus clipper l'icône */
  text-align: center;
}

/* Spacer qui réserve la place pour l'icône flottante au-dessus du card */
.success-icon-spacer {
  display: flex;
  justify-content: center;
  padding-top: 40px; /* crée l'espace interne, pas de margin négatif */
}

.success-icon-wrapper {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px -4px rgba(16,185,129,0.4);
  border: 4px solid white;
}

.success-title {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
}

.success-text {
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

/* Date Picker */
.v-picker >>> .v-picker__title {
  background: #1e40af !important;
}

.v-picker >>> .v-btn--active {
  background: #1e40af !important;
  color: white !important;
}

@media (max-width: 600px) {
  .info-page {
    padding: 16px !important;
  }

  .page-header {
    flex-direction: column;
    text-align: center;
  }

  .action-section {
    flex-direction: column-reverse;
    gap: 12px;
  }

  .save-btn,
  .cancel-btn {
    width: 100%;
    margin: 0;
  }
}
</style>